import { Box, Text, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { F0TagStatus } from '@factorialco/f0-react'
import { F0AvatarEmoji } from '@factorialco/f0-react'
import { BenefitsSummaryStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import { type BenefitListing, getBenefitsList } from 'modules/benefits/lib/benefitListings'
import { WizardData } from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal/types'

const BenefitsSummaryStep = ({
  data: _data,
  onDataChanged: _onDataChanged,
}: WizardStepRenderParams<WizardData>) => {
  const { company } = useCurrentSession()

  const { activeMarkets } = useActiveMarkets()

  const { data } = useQuery(BenefitsSummaryStepDocument, {
    skip: !activeMarkets?.length,
    fetchPolicy: 'cache-first',
  })

  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes

  const benefitsList: BenefitListing[] = useUnsafeMemo(
    () =>
      activeMarkets?.flatMap((market) =>
        getBenefitsList(market, featureAvailabilities).filter(
          (benefit) => benefit.availability === 'active'
        )
      ) ?? [],
    [activeMarkets, featureAvailabilities]
  )

  useEffect(() => {
    track(trackingEvents.benefits.userLandsInBenefitsActivationOnboarding, {
      companyId: company.id,
    })
  }, [company.id])

  return (
    <Box flexDirection="column" position="relative" justifyContent="center">
      <Box flexDirection="column" paddingBottom="s20">
        <Text.Normal weight="semibold" color="grey1000" size="400">
          {i18n.t('benefits.tabs.employees.activation_results.modal.review_benefits.step_title')}
        </Text.Normal>
        <Text.Normal weight="normal" color="grey800" size="300">
          {i18n.t('benefits.tabs.employees.activation_results.modal.review_benefits.description')}
        </Text.Normal>
      </Box>
      <Box flexDirection="column">
        {benefitsList.map((benefit) => (
          <Box key={benefit.title} paddingBottom="s12">
            <F0AvatarEmoji emoji={benefit.emoji} size="md" />

            <Box flexDirection="column" paddingTop="s12" alignItems="flexStart">
              <Text.Normal weight="semibold" color="grey1000" size="300">
                {benefit.title}
              </Text.Normal>

              <Text.Normal weight="normal" color="grey800" size="200">
                {benefit.reviewDescription || benefit.description}
              </Text.Normal>
            </Box>

            {benefit.availability === 'pending' && (
              <Box flexDirection="row" paddingTop="s8">
                <F0TagStatus
                  text={i18n.t(
                    'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.tags.coming_soon'
                  )}
                  variant="info"
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default BenefitsSummaryStep
